import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getDocsFromServer,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  orderBy,
} from 'firebase/firestore';

export type InvitationRole = 'coach' | 'player';
export type InvitationStatus = 'pending' | 'accepted';

export interface Invitation {
  id: string;
  email: string;
  role: InvitationRole;
  status: InvitationStatus;
  token: string;
  createdAt: string;
  createdBy: string;
  acceptedAt?: string;
  lastEmailSentAt?: string;
}

export interface ProspectiveUser {
  id: string;
  email: string;
  howDidYouHear: string;
  createdAt: string;
  userAgent?: string;
  referrer?: string;
}

// ── Invitations ──────────────────────────────────────────────

function generateToken(): string {
  const array = new Uint8Array(24);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

export async function createInvitation(
  email: string,
  role: InvitationRole,
  createdBy: string,
): Promise<Invitation> {
  const normalizedEmail = email.toLowerCase().trim();

  // Reject if a user account already exists or a pending invitation already exists
  const [existingUserSnap, existingInviteSnap] = await Promise.all([
    getDocs(query(collection(db, 'users'), where('email', '==', normalizedEmail))),
    getDocs(query(collection(db, 'invitations'), where('email', '==', normalizedEmail), where('status', '==', 'pending'))),
  ]);
  if (!existingUserSnap.empty) {
    throw new Error('A user account already exists with this email address.');
  }
  if (!existingInviteSnap.empty) {
    throw new Error('A pending invitation already exists for this email address.');
  }

  const token = generateToken();
  const ref = await addDoc(collection(db, 'invitations'), {
    email: normalizedEmail,
    role,
    status: 'pending',
    token,
    createdBy,
    createdAt: serverTimestamp(),
  });
  return getInvitationById(ref.id) as Promise<Invitation>;
}

export async function getInvitationById(id: string): Promise<Invitation | null> {
  const snap = await getDoc(doc(db, 'invitations', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Invitation;
}

export async function getInvitationByToken(token: string): Promise<Invitation | null> {
  const q = query(collection(db, 'invitations'), where('token', '==', token));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() } as Invitation;
}

export async function getInvitationByEmail(email: string): Promise<Invitation | null> {
  const q = query(
    collection(db, 'invitations'),
    where('email', '==', email.toLowerCase().trim()),
    where('status', '==', 'pending'),
  );
  const snap = await getDocsFromServer(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() } as Invitation;
}

export async function listInvitations(): Promise<Invitation[]> {
  const q = query(collection(db, 'invitations'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Invitation));
}

export async function markInvitationAccepted(id: string): Promise<void> {
  await updateDoc(doc(db, 'invitations', id), {
    status: 'accepted',
    acceptedAt: serverTimestamp(),
  });
}

export async function deleteInvitation(id: string): Promise<void> {
  await deleteDoc(doc(db, 'invitations', id));
}

// ── Prospective users (waitlist) ──────────────────────────────

export async function saveProspectiveUser(
  email: string,
  howDidYouHear: string,
): Promise<void> {
  await addDoc(collection(db, 'prospectiveUsers'), {
    email: email.toLowerCase().trim(),
    howDidYouHear,
    createdAt: serverTimestamp(),
    userAgent: navigator.userAgent,
    referrer: document.referrer || null,
  });
}

export async function listProspectiveUsers(): Promise<ProspectiveUser[]> {
  const q = query(collection(db, 'prospectiveUsers'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as ProspectiveUser));
}

export async function sendInvitationEmail(invitationId: string): Promise<void> {
  const fn = httpsCallable(functions, 'adminSendInvitationEmail');
  await fn({ invitationId });
}
